// ============================================================
// TMR Portal Admin — leituras e escritas de Taxas, sobre @tmr/api-client
// ============================================================
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { api } from '@tmr/api-client';
import { companyKeys } from '../queryClient';
import { singlePageReader } from '../../lib/export/exportPageReader';
import type {
  ApplyTemporaryRateRequest,
  CompanyProductList,
  CompanyRate,
  CompanyRateFilters,
  CompanyRateList,
  CreateRateRequest,
  EndTemporaryRateRequest,
  RateCatalog,
  RateDrift,
  RateHistory,
  RateSyncResult,
  RateWriteResult,
  UpdateRateRequest,
} from './contract';

/**
 * What the legacy is actually charging, compared with what the V2 exported.
 *
 * Deliberately not part of the list: it is a round trip to the legacy, and the list is read on every visit
 * to the tab. This is the check an operator asks for when a number looks wrong — and the only one that can
 * answer, because "aguardando sincronia" compares the V2 with its own memory (`15` §3 E).
 *
 * @param companyId The Empresa.
 * @param enabled Whether to run it — the tab asks only when the operator does.
 */
export function useRateDrift(companyId: string, enabled: boolean): UseQueryResult<RateDrift> {
  return useQuery({
    queryKey: companyKeys.rateDrift(companyId),
    queryFn: ({ signal }) => api.get<RateDrift>(`/companies/${companyId}/rates/drift`, { signal }),
    enabled,
    staleTime: 0,
  });
}

/**
 * One rate's recorded changes, newest first.
 *
 * Read only when the operator asks: most visits to the tab are about what a rate *is*, and the history is
 * the question that comes up when a number is being disputed. Never served from cache — the answer to "who
 * changed this" has to be the current one.
 *
 * @param companyId The Empresa.
 * @param rateId The rate, or undefined while no history is open.
 */
export function useRateHistory(
  companyId: string,
  rateId: string | undefined,
): UseQueryResult<RateHistory> {
  return useQuery({
    queryKey: companyKeys.rateHistory(companyId, rateId ?? ''),
    queryFn: ({ signal }) =>
      api.get<RateHistory>(`/companies/${companyId}/rates/${rateId}/history`, { signal }),
    enabled: Boolean(rateId),
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * The Empresa's products, for the rate form's product picker.
 *
 * Read only when a form is open: most Empresas are all Taxa Padrão, and this is a round trip to somebody
 * else's database for a field the operator may never touch.
 *
 * @param companyId The Empresa, or undefined while the form is closed.
 */
export function useCompanyProducts(companyId: string | undefined): UseQueryResult<CompanyProductList> {
  return useQuery({
    queryKey: companyKeys.rateProducts(companyId ?? ''),
    queryFn: ({ signal }) =>
      api.get<CompanyProductList>(`/companies/${companyId}/rates/products`, { signal }),
    enabled: Boolean(companyId),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * An Empresa's rates, both scopes, with the value that applies today.
 *
 * Not paginated, by design on the server: an Empresa's rates are compared against each other — which band
 * covers which installment count — and paging a comparison is worse than scrolling it.
 *
 * A pure read: it never brings rates over from the legacy. When there are none here yet and the Empresa
 * has a producer, the response says `needsImport` and the screen decides — bringing them over is
 * `POST /rates/sync`, gated by `company.rates.manage` (`15` §10, D-A2).
 *
 * @param companyId The Empresa, or undefined while the route param resolves.
 * @param filters Scope, rate type, product and temporary state. All optional.
 */
export function useCompanyRates(
  companyId: string | undefined,
  filters: CompanyRateFilters = {},
): UseQueryResult<CompanyRateList> {
  return useQuery({
    queryKey: companyKeys.rates(companyId ?? '', filters as Record<string, unknown>),
    queryFn: ({ signal }) => {
      const search = new URLSearchParams();
      if (filters.scope) search.set('scope', filters.scope);
      if (filters.rateTypeId != null) search.set('rateTypeId', String(filters.rateTypeId));
      if (filters.productId != null) search.set('productId', String(filters.productId));
      if (filters.temporary) search.set('temporary', filters.temporary);

      const query = search.toString();
      return api.get<CompanyRateList>(
        `/companies/${companyId}/rates${query ? `?${query}` : ''}`,
        { signal },
      );
    },
    enabled: Boolean(companyId),
  });
}

/**
 * Reads one Empresa's rates for **export** (20 §3).
 *
 * Not paged, and should not be: an Empresa has tens of rates, not thousands, and the tab already reads
 * them whole. It arrives as a single page so the export kit needs no second kind of source.
 *
 * @param companyId The Empresa whose rates to export.
 */
export function companyRateExportPage(companyId: string) {
  return singlePageReader<CompanyRate>(
    async () => (await api.get<CompanyRateList>(`/companies/${companyId}/rates`)).items,
  );
}

/**
 * The rate-type and credit-engine vocabularies (`08` §5).
 *
 * Read from the legacy and never owned here, so the form offers what the other system actually has — and
 * gains a sixth rate type the day somebody adds one, without a deploy. Cached for longer than a normal
 * query because these change about never.
 */
export function useRateCatalog(): UseQueryResult<RateCatalog> {
  return useQuery({
    queryKey: companyKeys.rateCatalog,
    queryFn: ({ signal }) => api.get<RateCatalog>('/companies/rate-catalog', { signal }),
    staleTime: 10 * 60_000,
  });
}

/** Creates a rate for an Empresa. */
export function useCreateRate(companyId: string) {
  return useRateMutation(companyId, (request: CreateRateRequest) =>
    api.post<RateWriteResult>(`/companies/${companyId}/rates`, request));
}

/** Replaces a rate's definition. The temporary window, if any, is left in place. */
export function useUpdateRate(companyId: string) {
  return useRateMutation(
    companyId,
    ({ rateId, request }: { rateId: string; request: UpdateRateRequest }) =>
      api.put<RateWriteResult>(`/companies/${companyId}/rates/${rateId}`, request));
}

/** Removes a rate, here and in the legacy. */
export function useDeleteRate(companyId: string) {
  return useRateMutation(companyId, (rateId: string) =>
    api.delete<RateWriteResult>(`/companies/${companyId}/rates/${rateId}`));
}

/** Puts a temporary rate on a rate, for a closed window. */
export function useApplyTemporaryRate(companyId: string) {
  return useRateMutation(
    companyId,
    ({ rateId, request }: { rateId: string; request: ApplyTemporaryRateRequest }) =>
      api.post<RateWriteResult>(`/companies/${companyId}/rates/${rateId}/temporary`, request));
}

/** Ends a temporary rate, returning the rate — and its cascaded counterparts — to the standard value. */
export function useEndTemporaryRate(companyId: string) {
  return useRateMutation(
    companyId,
    ({ rateId, request }: { rateId: string; request: EndTemporaryRateRequest }) =>
      api.post<RateWriteResult>(`/companies/${companyId}/rates/${rateId}/temporary/end`, request));
}

/**
 * Reconciles the Empresa's rates with the legacy on demand.
 *
 * It has a button of its own because the legacy keeps changing rates behind our back and always will while
 * it is alive: applying a coupon rewrites values in bulk, and creating a product copies the Empresa's rates
 * into a new set of rows.
 */
export function useSyncRates(companyId: string) {
  return useRateMutation(companyId, () =>
    api.post<RateSyncResult>(`/companies/${companyId}/rates/sync`, {}));
}

/**
 * Shared plumbing for every rate write: run it, then invalidate the Empresa's rates.
 *
 * Invalidating rather than patching the cache is what keeps the screen honest about the cascade — one call
 * can move several rates, and the effective value of each depends on today's date resolved by the server.
 *
 * @param companyId The Empresa whose rate cache to refresh.
 * @param mutationFn The request itself.
 */
function useRateMutation<TVariables, TResult>(
  companyId: string,
  mutationFn: (variables: TVariables) => Promise<TResult>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: companyKeys.ratesAll(companyId) });
    },
  });
}
